import type { CrosswordConfig, CrosswordPuzzle, CrosswordWord } from '../../types'
import { getWordBank, type WordEntry } from './wordbank'

// ─── Types ────────────────────────────────────────────────────────────────────

type Dir = 'across' | 'down'

interface PlacedEntry extends WordEntry {
  row: number
  col: number
  dir: Dir
}

// ─── Grid helpers ─────────────────────────────────────────────────────────────

const G = 15  // internal grid size

function makeGrid(): string[][] {
  return Array.from({ length: G }, () => new Array<string>(G).fill(''))
}

function placeOnGrid(grid: string[][], word: string, r: number, c: number, dir: Dir): void {
  const dr = dir === 'down' ? 1 : 0
  const dc = dir === 'across' ? 1 : 0
  for (let i = 0; i < word.length; i++) {
    grid[r + dr * i][c + dc * i] = word[i]
  }
}

// ─── Placement validation ─────────────────────────────────────────────────────

function canPlace(
  grid: string[][],
  word: string,
  r: number,
  c: number,
  dir: Dir,
  isFirst = false,
): boolean {
  const dr = dir === 'down' ? 1 : 0
  const dc = dir === 'across' ? 1 : 0
  const endR = r + dr * (word.length - 1)
  const endC = c + dc * (word.length - 1)

  // Out of bounds
  if (r < 0 || c < 0 || endR >= G || endC >= G) return false

  // Cell immediately before must be empty
  const preR = r - dr, preC = c - dc
  if (preR >= 0 && preC >= 0 && grid[preR][preC] !== '') return false

  // Cell immediately after must be empty
  const postR = endR + dr, postC = endC + dc
  if (postR < G && postC < G && grid[postR][postC] !== '') return false

  let crossings = 0

  for (let i = 0; i < word.length; i++) {
    const ri = r + dr * i
    const ci = c + dc * i
    const cell = grid[ri][ci]

    if (cell !== '') {
      if (cell !== word[i]) return false  // letter conflict
      crossings++
      // At a crossing cell we don't check perpendicular neighbors — the other word owns those cells
      continue
    }

    // Empty cell: no parallel neighbor allowed (prevents adjacent parallel words)
    if (dir === 'across') {
      if (ri > 0     && grid[ri - 1][ci] !== '') return false
      if (ri < G - 1 && grid[ri + 1][ci] !== '') return false
    } else {
      if (ci > 0     && grid[ri][ci - 1] !== '') return false
      if (ci < G - 1 && grid[ri][ci + 1] !== '') return false
    }
  }

  return isFirst || crossings > 0
}

// ─── Find best placement for a new word ──────────────────────────────────────

function findBestPlacement(
  grid: string[][],
  word: string,
  placed: PlacedEntry[],
): { row: number; col: number; dir: Dir } | null {
  let best: { row: number; col: number; dir: Dir; score: number } | null = null

  for (const p of placed) {
    const perpDir: Dir = p.dir === 'across' ? 'down' : 'across'
    const dr = perpDir === 'down' ? 1 : 0
    const dc = perpDir === 'across' ? 1 : 0

    for (let pi = 0; pi < p.word.length; pi++) {
      for (let ni = 0; ni < word.length; ni++) {
        if (p.word[pi] !== word[ni]) continue

        // Position of the shared letter on the existing word
        const crossR = p.dir === 'down' ? p.row + pi : p.row
        const crossC = p.dir === 'across' ? p.col + pi : p.col

        // Start of the new word
        const newRow = perpDir === 'down' ? crossR - ni : crossR
        const newCol = perpDir === 'across' ? crossC - ni : crossC

        if (!canPlace(grid, word, newRow, newCol, perpDir)) continue

        // Score: extra crossings bonus + center proximity bonus
        let score = 0
        for (let i = 0; i < word.length; i++) {
          if (grid[newRow + dr * i][newCol + dc * i] !== '') score += 3
        }
        const midR = newRow + dr * (word.length / 2)
        const midC = newCol + dc * (word.length / 2)
        score -= (Math.abs(midR - G / 2) + Math.abs(midC - G / 2)) * 0.05

        if (!best || score > best.score) {
          best = { row: newRow, col: newCol, dir: perpDir, score }
        }
      }
    }
  }

  return best ? { row: best.row, col: best.col, dir: best.dir } : null
}

// ─── Fisher-Yates shuffle ─────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// ─── Target word counts per size ─────────────────────────────────────────────

const TARGET_WORDS: Record<string, number> = {
  small:  8,
  medium: 12,
  large:  16,
}

// ─── Main generator ───────────────────────────────────────────────────────────

export function generateCrossword(config: CrosswordConfig, wordEntries?: WordEntry[]): CrosswordPuzzle {
  const bank   = shuffle(wordEntries ?? getWordBank(config.language, config.ageGroup))
  const target = TARGET_WORDS[config.size] ?? 10
  const grid   = makeGrid()
  const placed: PlacedEntry[] = []

  // Place first word horizontally at grid center
  const first = bank[0]
  const startRow = Math.floor(G / 2)
  const startCol = Math.floor((G - first.word.length) / 2)
  placeOnGrid(grid, first.word, startRow, startCol, 'across')
  placed.push({ ...first, row: startRow, col: startCol, dir: 'across' })

  // Greedily place remaining words
  for (let i = 1; i < bank.length && placed.length < target; i++) {
    const entry = bank[i]
    const pos   = findBestPlacement(grid, entry.word, placed)
    if (pos) {
      placeOnGrid(grid, entry.word, pos.row, pos.col, pos.dir)
      placed.push({ ...entry, ...pos })
    }
  }

  // Trim grid: find bounding box of placed words
  let minR = G, maxR = 0, minC = G, maxC = 0
  for (const p of placed) {
    minR = Math.min(minR, p.row)
    minC = Math.min(minC, p.col)
    const endR = p.dir === 'down' ? p.row + p.word.length - 1 : p.row
    const endC = p.dir === 'across' ? p.col + p.word.length - 1 : p.col
    maxR = Math.max(maxR, endR)
    maxC = Math.max(maxC, endC)
  }

  // Adjust positions to be 0-based
  const trimmed = placed.map((p) => ({ ...p, row: p.row - minR, col: p.col - minC }))
  const rows = maxR - minR + 1
  const cols = maxC - minC + 1

  // Assign clue numbers: sort starts top-to-bottom, left-to-right
  const startKeys = new Map<string, number>()
  const startCells = trimmed
    .map((p) => ({ row: p.row, col: p.col, key: `${p.row},${p.col}` }))
    .filter((v, i, arr) => arr.findIndex((x) => x.key === v.key) === i)
    .sort((a, b) => a.row !== b.row ? a.row - b.row : a.col - b.col)
  startCells.forEach((s, i) => startKeys.set(s.key, i + 1))

  const words: CrosswordWord[] = trimmed.map((p) => ({
    word:      p.word,
    clue:      p.clue,
    direction: p.dir,
    row:       p.row,
    col:       p.col,
    number:    startKeys.get(`${p.row},${p.col}`) ?? 0,
  }))

  return { config, words, rows, cols }
}

// ─── Cell map helper (exported for use in grid + PDF) ────────────────────────

export interface CellInfo {
  across?: CrosswordWord
  down?:   CrosswordWord
  number?: number
}

export function buildCellMap(puzzle: CrosswordPuzzle): Map<string, CellInfo> {
  const map = new Map<string, CellInfo>()

  for (const word of puzzle.words) {
    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0
    for (let i = 0; i < word.word.length; i++) {
      const r = word.row + dr * i
      const c = word.col + dc * i
      const key = `${r},${c}`
      const existing = map.get(key) ?? {}
      if (i === 0) existing.number = word.number
      existing[word.direction] = word
      map.set(key, existing)
    }
  }

  return map
}
